import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import UnifiedMap from '../../components/UnifiedMap';
import BBoxOverlay from '../../components/BBoxOverlay';
import { getApiBaseUrl, getImageUrl } from '../../config/runtime';
import { normalizeTaxonomyHierarchyEntries } from '../../utils/taxonomy';

function parseBBoxParam(bboxStr) {
  if (!bboxStr) return null;
  const parts = bboxStr.split(',').map(parseFloat);
  if (parts.length !== 4 || parts.some(isNaN)) return null;
  return parts;
}

// Cache configuration
const CACHE_MAX_SIZE = 25; // Reduced from 50 to prevent memory bloat
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache TTL

// Unified card component for all related content
function RelatedCard({ item, type, onClick, onNavigate }) {
  const renderContent = () => {
    switch (type) {
      case 'detection':
        return (
          <>
            <div className="text-xs font-medium text-gray-900">{item.label || 'Unknown'}</div>
            <div className="text-xs text-gray-500 mt-1">
              {item.confidence != null ? (item.confidence * 100).toFixed(0) : '?'}% confidence
              {item.category && ` • ${item.category}`}
            </div>
            {item.captured_at && (
              <div className="text-xs text-gray-400 mt-1">
                {new Date(item.captured_at).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            )}
          </>
        );
      case 'observation':
        return (
          <>
            <div className="text-xs font-medium text-gray-900">
              {item.captured_at ? new Date(item.captured_at).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              }) : 'No date'}
            </div>
            <div className="text-xs text-gray-500 font-mono mt-1">
              {item.lat != null && item.lng != null 
                ? `${item.lat.toFixed(3)}, ${item.lng.toFixed(3)}` 
                : 'No location'}
            </div>
          </>
        );
      case 'thing':
        return (
          <>
            <div className="text-xs font-medium text-gray-900">{item.canonical_name || 'Unnamed thing'}</div>
            <div className="text-xs text-gray-500 mt-1">
              {item.observation_count || 0} observations • {item.detection_count || 0} detections
            </div>
            {item.taxonomic_classification && (
              <div className="text-[10px] text-gray-500 mt-1">
                {(item.taxonomic_classification.genus || item.taxonomic_classification.species) && (
                  <span>
                    {item.taxonomic_classification.genus ? `${item.taxonomic_classification.genus}` : ''}
                    {item.taxonomic_classification.species ? ` ${item.taxonomic_classification.species}` : ''}
                  </span>
                )}
              </div>
            )}
          </>
        );
      default:
        return null;
    }
  };

  // For things, use sample_images; for observations/detections, use image_url
  const rawImageUrl = item.sample_images?.[0] || item.image_url || null;
  const imageUrl = rawImageUrl ? getImageUrl(rawImageUrl) : null;

  // Debug logging for RelatedCard image investigation
  if (process.env.NODE_ENV !== 'production') {
    console.log('[ThingsExplorer] RelatedCard item:', {
      id: item.id,
      type: type,
      canonical_name: item.canonical_name,
      sample_images: item.sample_images,
      image_url: item.image_url,
      finalImageUrl: imageUrl,
      hasImage: !!imageUrl
    });
  }

  const handleClick = () => {
    if (onNavigate) {
      onNavigate(item, type);
    } else if (onClick) {
      onClick();
    }
  };

  return (
    <div 
      className="rounded-xl border border-gray-200 hover:border-gray-400 bg-white hover:bg-gray-50 cursor-pointer transition-all"
      onClick={handleClick}
    >
      <div className="flex gap-3 p-3">
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt="" 
            className="w-12 h-12 object-cover rounded-xl flex-shrink-0 border border-gray-200" 
          />
        ) : (
          <div className="w-12 h-12 bg-gray-100 rounded-xl flex-shrink-0 border border-gray-200"></div>
        )}
        <div className="flex-1 min-w-0">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

export default function ThingsExplorer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [clusters, setClusters] = useState([]); // Reserved for future clustering UI
  const [messagePins, setMessagePins] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null); // Store the clicked item's data
  const [showObsCount, setShowObsCount] = useState(10); // Pagination for observations
  const [relatedView, setRelatedView] = useState('default'); // 'default', 'observations', 'detections', 'things'
  const [relatedData, setRelatedData] = useState(null); // Store fetched related data (detections, observation)
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pinClickLoading, setPinClickLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0); // Total count from API
  const [hasMore, setHasMore] = useState(false); // Whether more items are available
  const [loadingMore, setLoadingMore] = useState(false); // Loading state for "load more"
  const mapRef = useRef(null);
  const cacheRef = useRef(new Map());
  const firstLoadRef = useRef(true);
  const listRef = useRef(null);
  const [taxaQ, setTaxaQ] = useState('');
  const [taxaOpts, setTaxaOpts] = useState([]);
  const taxaDebounceRef = useRef(null);
  const detailAbortControllerRef = useRef(null);
  const relatedDataAbortControllerRef = useRef(null);
  const detectionHierarchyEntries = normalizeTaxonomyHierarchyEntries(
    selectedItem?.taxonomic_classification?.hierarchy
  );
  const thingHierarchyEntries = normalizeTaxonomyHierarchyEntries(
    detail?.taxonomic_classification?.hierarchy
  );

  // Helper function to reset pagination state
  const resetPaginationState = () => {
    setTotalCount(0);
    setHasMore(false);
    setLoadingMore(false);
  };

  // Cleanup function to prevent memory leaks
  const cleanup = () => {
    try {
      // Clear cache
      if (cacheRef.current) {
        cacheRef.current.clear();
      }
      
      // Clear debounced functions
      if (taxaDebounceRef.current) {
        clearTimeout(taxaDebounceRef.current);
        taxaDebounceRef.current = null;
      }
      
      // Abort pending requests
      if (detailAbortControllerRef.current) {
        detailAbortControllerRef.current.abort();
        detailAbortControllerRef.current = null;
      }
      if (relatedDataAbortControllerRef.current) {
        relatedDataAbortControllerRef.current.abort();
        relatedDataAbortControllerRef.current = null;
      }
      
      // Reset refs
      firstLoadRef.current = true;
      
      // Clear state
      setItems([]);
      setClusters([]);
      setMessagePins([]);
      setSelectedId(null);
      setSelectedItem(null);
      setRelatedData(null);
      setDetail(null);
      setTaxaOpts([]);
      setTotalCount(0);
      setHasMore(false);
      setLoadingMore(false);
      
    } catch (e) {
      console.warn('[ThingsExplorer] Cleanup error:', e);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, []);


  const query = useMemo(() => {
    return {
      search: searchParams.get('search') || '',
      bbox: searchParams.get('bbox') || '',
      type: searchParams.get('type') || '',
      minConfidence: searchParams.get('minConfidence') || '0',
      category: searchParams.get('category') || '',
      from: searchParams.get('from') || '',
      to: searchParams.get('to') || '',
      page: searchParams.get('page') || '1',
      limit: searchParams.get('limit') || '50',
      layer: searchParams.get('layer') || 'things',
      cluster: searchParams.get('cluster') || 'true',
      project_id: searchParams.get('project_id') || '',
    };
  }, [searchParams]);

  // Fetch data (debounced) with TTL cache
  useEffect(() => {
    const controller = new AbortController();
    let t;
    async function fetchThings() {
      setLoading(true);
      setError(null);
      try {
        // Ensure clusters are returned on first load by providing a world bbox when none is set
        const effective = { ...query };
        if ((effective.cluster !== 'false') && (!effective.bbox || effective.bbox === '')) {
          effective.bbox = '-180,-85,180,85';
        }
        const qs = new URLSearchParams(
          Object.entries(effective).filter(([_, v]) => v !== '' && v !== null)
        ).toString();
        
        const currentPage = parseInt(query.page) || 1;
        
        // Check cache with TTL (only for page 1 to avoid pagination state issues)
        if (currentPage === 1 && cacheRef.current.has(qs)) {
          const cached = cacheRef.current.get(qs);
          const age = Date.now() - cached.timestamp;
          if (age < CACHE_TTL) {
            setItems(cached.things);
            setClusters(cached.clusters);
            setMessagePins(cached.pins);
            setTotalCount(cached.total || 0);
            setHasMore(cached.hasMore || false);
            setLoading(false);
            return;
          } else {
            // Expired - remove from cache
            cacheRef.current.delete(qs);
          }
        }
        const apiBase = getApiBaseUrl().replace(/\/$/, '');
        const res = await fetch(`${apiBase}/thingsdb/things?${qs}`, { signal: controller.signal });
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data.things) ? data.things : [];
        const cl = Array.isArray(data.clusters) ? data.clusters : [];
        const total = data.pagination?.total || 0;
        const limit = parseInt(query.limit) || 50;
        
        console.log('[ThingsExplorer] Query:', Object.fromEntries(new URLSearchParams(qs)));
        console.log('[ThingsExplorer] Results:', { count: list.length, first: list[0], total, currentPage, limit });
        
        // Handle pagination: append for subsequent pages, replace for first page or new queries
        if (currentPage === 1) {
          setItems(list);
          setHasMore(list.length === limit && list.length < total);
        } else {
          setItems(prev => {
            // Deduplicate items by ID to prevent React key conflicts
            const existingIds = new Set(prev.map(item => item.id));
            const newUniqueItems = list.filter(item => !existingIds.has(item.id));
            const combinedItems = [...prev, ...newUniqueItems];
            setHasMore(list.length === limit && combinedItems.length < total);
            return combinedItems;
          });
        }
        
        setClusters(cl);
        setTotalCount(total);

        // Build pins for map (dedupe keys)
        const pins = [];
        const pinKeySet = new Set();
        // Clusters from API
        if (query.cluster !== 'false' && cl.length > 0) {
          cl.forEach((c, idx) => {
            if (c && typeof c.lat === 'number' && typeof c.lng === 'number' && c.count > 0) {
              const id = `c-${idx}`;
              const pkey = `cluster-${id}-${c.lat}-${c.lng}`;
              if (pinKeySet.has(pkey)) return;
              pinKeySet.add(pkey);
              pins.push({
                id,
                type: 'cluster',
                count: c.count,
                location: { lat: c.lat, lng: c.lng },
                centerPin: { type: 'PHOTO' },
              });
            }
          });
        }
        // Individual points
        list.forEach((it) => {
          if (typeof it.lat === 'number' && typeof it.lng === 'number') {
            const pkey = `pin-${it.id}-${it.lat}-${it.lng}`;
            if (pinKeySet.has(pkey)) return;
            pinKeySet.add(pkey);
            // For non-things layers, prefer thing_id for detail fetching
            const detailId = query.layer === 'things' ? it.id : (it.thing_id || null);
            pins.push({
              id: pkey,
              tid: detailId,
              type: 'pin',
              location: { lat: it.lat, lng: it.lng },
              centerPin: {
                type: 'PHOTO',
                imageUrl: Array.isArray(it.sample_images) && it.sample_images.length > 0 ? getImageUrl(it.sample_images[0]) : null,
                // Add detection metadata for popup
                detectionLabel: it.canonical_name || it.label || 'Unknown',
                confidence: it.confidence,
                timestamp: it.last_seen_at || it.captured_at,
                bbox: it.bbox,
                category: it.category || it.type,
              },
            });
          }
        });
        setMessagePins(pins);
        // Clear selection on new result set
        try { setSelectedId(null); setSelectedItem(null); } catch (e) {
          console.warn('[ThingsExplorer] Failed to clear selection:', e);
        }
        
        // Cache with timestamp and enforce max size (only cache page 1)
        if (currentPage === 1) {
          if (cacheRef.current.size >= CACHE_MAX_SIZE) {
            // Remove oldest entries (remove 25% of cache to prevent frequent evictions)
            const keysToRemove = Array.from(cacheRef.current.keys()).slice(0, Math.floor(CACHE_MAX_SIZE * 0.25));
            keysToRemove.forEach(key => cacheRef.current.delete(key));
          }
          cacheRef.current.set(qs, { 
            things: list, 
            clusters: cl, 
            pins,
            total: total,
            hasMore: list.length === limit && list.length < total,
            timestamp: Date.now()
          });
        }

        // Fit to results on first load or when no bbox is set
        const bboxStr = query.bbox;
        if (mapRef.current && (firstLoadRef.current || !bboxStr)) {
          const latlngs = [];
          pins.forEach((p) => {
            if (p.location) latlngs.push([p.location.lat, p.location.lng]);
          });
          if (latlngs.length > 0 && window.L && window.L.latLngBounds) {
            try {
              const b = window.L.latLngBounds(latlngs);
              mapRef.current.fitBounds(b, { padding: [20, 20] });
              // Do not write bbox to URL; keep sidebar results decoupled from map viewport
            } catch (e) {
              console.warn('[ThingsExplorer] Map fit bounds failed:', e);
            }
          }
          firstLoadRef.current = false;
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          setError(e.message || 'Failed to load things');
          toast.error('Failed to load things');
        }
      } finally {
        setLoading(false);
      }
    }
    t = setTimeout(fetchThings, 250);
    return () => { 
      try { clearTimeout(t); } catch (e) {
        console.warn('[ThingsExplorer] Failed to clear timeout:', e);
      }
      controller.abort(); 
    };
  }, [query]);

  // Decouple sidebar results from map viewport: do not sync bbox to URL on move

  const handleItemClick = (it) => {
    const map = mapRef.current;
    if (map && typeof it.lat === 'number' && typeof it.lng === 'number') {
      try {
        map.setView([it.lat, it.lng], Math.max(map.getZoom(), 14), { animate: true });
      } catch (e) {
        console.warn('[ThingsExplorer] Map setView failed:', e);
      }
    }
    // Use the item's actual id for selection (to avoid highlighting multiple items)
    setSelectedId(it.id);
    setSelectedItem(it); // Store the clicked item
    
    // Debug logging for detection bbox data
    if (process.env.NODE_ENV !== 'production' && query.layer === 'detections') {
      console.debug('[ThingsExplorer] Detection clicked:', {
        id: it.id,
        label: it.canonical_name,
        bbox: it.bbox,
        bboxType: typeof it.bbox,
        hasBbox: !!it.bbox
      });
    }
    
    // But fetch the thing detail using thing_id for detections/observations
    const detailId = query.layer === 'things' ? it.id : it.thing_id;
    if (detailId) {
      fetchDetail(detailId);
    }
    // When clicking a detection, default the right pane related view to Observation and fetch it
    try {
      if (query.layer === 'detections' && it.observation_id) {
        setRelatedView('observations');
        fetchRelatedData('observations', it.observation_id);
      }
    } catch (e) {
      console.warn('[ThingsExplorer] Failed to preload observation for detection:', e);
    }
    // Scroll selected item into view
    try {
      const el = listRef.current?.querySelector(`[data-item-key="${it.id}"]`);
      el && el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch (e) {
      console.warn('[ThingsExplorer] Scroll into view failed:', e);
    }
  };

  // Handle navigation from related items
  const handleRelatedItemNavigate = (item, type) => {
    try {
      // Clear current selection and related data
      setSelectedId(null);
      setSelectedItem(null);
      setRelatedData(null);
      setRelatedView('default');
      setDetail(null);
      
      // Navigate to the appropriate layer and select the item
      const next = new URLSearchParams(searchParams);
      
      switch (type) {
        case 'thing':
          next.set('layer', 'things');
          next.set('page', '1'); // Reset pagination
          setSearchParams(next);
          // The useEffect will reload the sidebar and we'll select the item
          setTimeout(() => {
            const thingItem = items.find(i => i.id === item.id);
            if (thingItem) {
              handleItemClick(thingItem);
            }
          }, 200);
          break;
          
        case 'observation':
          next.set('layer', 'observations');
          next.set('page', '1'); // Reset pagination
          setSearchParams(next);
          // The useEffect will reload the sidebar and we'll select the item
          setTimeout(() => {
            const obsItem = items.find(i => i.id === item.id);
            if (obsItem) {
              handleItemClick(obsItem);
            }
          }, 200);
          break;
          
        case 'detection':
          next.set('layer', 'detections');
          next.set('page', '1'); // Reset pagination
          setSearchParams(next);
          // The useEffect will reload the sidebar and we'll select the item
          setTimeout(() => {
            const detItem = items.find(i => i.id === item.id);
            if (detItem) {
              handleItemClick(detItem);
            }
          }, 200);
          break;
          
        default:
          console.warn('[ThingsExplorer] Unknown related item type:', type);
      }
    } catch (e) {
      console.warn('[ThingsExplorer] Related item navigation failed:', e);
    }
  };

  // Fetch related data based on view
  async function fetchRelatedData(view, id, page = 1) {
    if (!id || !view || view === 'default') return;
    
    // Cancel previous related data request
    if (relatedDataAbortControllerRef.current) {
      relatedDataAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    relatedDataAbortControllerRef.current = controller;
    
    try {
      setRelatedLoading(true);
      if (page === 1) {
        setRelatedData(null);
      }
      
      if (view === 'detections' && query.layer === 'things') {
        // Fetch detections for a thing
        const apiBase = getApiBaseUrl().replace(/\/$/, '');
        const res = await fetch(`${apiBase}/thingsdb/things/${id}/detections?limit=20`, { signal: controller.signal });
        if (res.ok) {
          const data = await res.json();
          if (page === 1) {
            setRelatedData(data);
          } else {
            // Append to existing data with deduplication
            setRelatedData(prev => {
              const existingDetections = prev?.detections || [];
              const existingIds = new Set(existingDetections.map(d => d.id));
              const newUniqueDetections = (data.detections || []).filter(d => !existingIds.has(d.id));
              return {
                ...prev,
                detections: [...existingDetections, ...newUniqueDetections]
              };
            });
          }
        } else {
          console.warn('[ThingsExplorer] Failed to fetch thing detections:', res.status, res.statusText);
          setRelatedData({ detections: [] });
        }
      } else if (view === 'detections' && query.layer === 'observations') {
        // Fetch detections for an observation
        const apiBase = getApiBaseUrl().replace(/\/$/, '');
        const res = await fetch(`${apiBase}/thingsdb/observations/${id}`, { signal: controller.signal });
        if (res.ok) {
          const data = await res.json();
          setRelatedData(data);
        } else {
          console.warn('[ThingsExplorer] Failed to fetch observation detections:', res.status, res.statusText);
          setRelatedData({ detections: [] });
        }
      } else if (view === 'observations' && query.layer === 'detections') {
        // Fetch observation for a detection
        const obsId = id; // id should be observation_id from selectedItem
        if (obsId) {
          const apiBase = getApiBaseUrl().replace(/\/$/, '');
          const res = await fetch(`${apiBase}/thingsdb/observations/${obsId}`, { signal: controller.signal });
          if (res.ok) {
            const contentType = res.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              console.warn('[ThingsExplorer] Non-JSON response for observation:', contentType);
              setRelatedData(null);
              return;
            }
            const data = await res.json();
            setRelatedData(data);
          } else {
            console.warn('[ThingsExplorer] Failed to fetch observation:', res.status, res.statusText);
            setRelatedData(null);
          }
        } else {
          console.warn('[ThingsExplorer] No observation_id available for detection');
          setRelatedData(null);
        }
      }
    } catch (err) {
      // Don't show error for aborted requests
      if (err.name === 'AbortError') {
        return;
      }
      console.error('Failed to fetch related data:', err);
      toast.error('Failed to load related data');
    } finally {
      // Only clear loading state if this request wasn't aborted
      if (!controller.signal.aborted) {
        setRelatedLoading(false);
        relatedDataAbortControllerRef.current = null;
      }
    }
  }

  async function fetchDetail(id) {
    // Cancel previous detail request
    if (detailAbortControllerRef.current) {
      detailAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    detailAbortControllerRef.current = controller;
    
    try {
      setDetailLoading(true);
      // Reset pagination and related view when loading new detail
      setShowObsCount(10);
      setRelatedView('default');
      setRelatedData(null);
      const apiBase = getApiBaseUrl().replace(/\/$/, '');
      const res = await fetch(`${apiBase}/thingsdb/things/${id}`, { signal: controller.signal });
      
      if (res.status === 404) {
        toast.error('Thing not found');
        setDetail(null);
        return;
      }
      
      if (!res.ok) {
        const contentType = res.headers.get('content-type');
        let errorMessage = `Failed: ${res.status}`;
        if (contentType && contentType.includes('application/json')) {
          try {
            const errorData = await res.json();
            errorMessage = errorData.detail || errorData.message || errorMessage;
          } catch (e) {
            errorMessage = res.statusText || errorMessage;
          }
        }
        throw new Error(errorMessage);
      }
      
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Expected JSON but got ${contentType}. Response: ${text.substring(0, 100)}`);
      }
      
      const data = await res.json();
      // Only update state if request wasn't aborted
      if (!controller.signal.aborted) {
        setDetail(data);
      }
    } catch (e) {
      // Don't show error for aborted requests
      if (e.name === 'AbortError') {
        return;
      }
      console.error('Failed to fetch thing detail:', e);
      toast.error(e.message || 'Failed to load thing detail');
      setDetail(null);
    } finally {
      // Only clear loading state if this request wasn't aborted
      if (!controller.signal.aborted) {
        setDetailLoading(false);
        detailAbortControllerRef.current = null;
      }
    }
  }

  // Map pin click → pan, select, open detail, and sync sidebar
  const handlePinClick = (id, meta) => {
    setPinClickLoading(true);
    try {
      if (meta && meta.location && mapRef.current) {
        mapRef.current.setView([meta.location.lat, meta.location.lng], Math.max(mapRef.current.getZoom(), 14), { animate: true });
      }
    } catch (e) {
      console.warn('[ThingsExplorer] Pin click map setView failed:', e);
    }
    if (id) {
      // Extract the actual item ID from the pin ID (format: pin-{itemId}-{lat}-{lng})
      const itemId = id.startsWith('pin-') ? id.split('-')[1] : id;
      setSelectedId(itemId);
      
      // Find the corresponding item from the current items list to get bbox data
      const clickedItem = items.find(item => item.id === itemId);
      if (clickedItem) {
        setSelectedItem(clickedItem);
      }
      
      // Use the tid (thing_id) for detail fetching, or fall back to itemId
      const detailId = meta?.tid || itemId;
      fetchDetail(detailId).finally(() => {
        setPinClickLoading(false);
      });
      
      try {
        const el = listRef.current?.querySelector(`[data-item-key="${itemId}"]`);
        el && el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } catch (e) {
        console.warn('[ThingsExplorer] Pin click scroll failed:', e);
      }
    } else {
      setPinClickLoading(false);
    }
  };

  // Taxa typeahead
  useEffect(() => {
    if (!taxaQ) { 
      setTaxaOpts([]); 
      return; 
    }
    
    // Clear existing timeout
    if (taxaDebounceRef.current) {
      clearTimeout(taxaDebounceRef.current);
      taxaDebounceRef.current = null;
    }
    
    // Set new timeout
    taxaDebounceRef.current = setTimeout(async () => {
      try {
        const apiBase = getApiBaseUrl().replace(/\/$/, '');
        const res = await fetch(`${apiBase}/thingsdb/taxa/search?q=${encodeURIComponent(taxaQ)}&lang=en&limit=8`);
        if (!res.ok) return;
        const data = await res.json();
        setTaxaOpts(Array.isArray(data.results) ? data.results : []);
      } catch (e) {
        console.warn('[ThingsExplorer] Taxa search failed:', e);
      }
    }, 250);
    
    // Cleanup function
    return () => { 
      if (taxaDebounceRef.current) {
        clearTimeout(taxaDebounceRef.current);
        taxaDebounceRef.current = null;
      }
    };
  }, [taxaQ]);

    return (
      <div className="w-full h-full flex">
        <div className="w-96 max-w-full border-r flex flex-col bg-gray-50">
          {/* HEADER - Fixed, elevated */}
          <div className="bg-white border-b shadow-sm">
            {/* <div className="px-6 py-2">
              <div className="flex items-baseline justify-between mb-1">
                <h1 className="text-2xl font-light tracking-tight text-gray-900">ThingsDB</h1>
              </div>
            </div> */}

            {/* FILTERS - Collapsed/Expandable sections for reduced noise */}
            <div className="px-6 pb-4 space-y-3 border-t-0 pt-4">
              {/* Layer Selection - Most important control */}
              <div>
                <div className="grid grid-cols-3 gap-1 bg-gray-200/80 dark:bg-gray-600/50 p-1 rounded-full">
                  {['things', 'observations', 'detections'].map((l) => (
                    <button
                      key={l}
                      onClick={() => {
                        const next = new URLSearchParams(searchParams);
                        next.set('layer', l);
                        next.delete('bbox');
                        next.set('page', '1');
                        try { firstLoadRef.current = true; } catch (e) {
                          console.warn('[ThingsExplorer] Failed to reset firstLoad:', e);
                        }
                        try { cacheRef.current = new Map(); } catch (e) {
                          console.warn('[ThingsExplorer] Failed to reset cache:', e);
                        }
                        // Close detail panel when switching layers
                        setDetail(null);
                        setSelectedItem(null);
                        setSelectedId(null);
                        // Reset pagination state when switching layers
                        resetPaginationState();
                        setSearchParams(next);
                      }}
                      className={`px-2 py-1.5 text-xs font-medium rounded-full transition-all ${
                        query.layer === l 
                          ? 'bg-white text-gray-900 shadow-sm' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      {l.charAt(0).toUpperCase() + l.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              {/* Primary Search */}
              <input
                type="text"
                placeholder="Search by name..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
                defaultValue={query.search}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const v = e.currentTarget.value || '';
                    const next = new URLSearchParams(searchParams);
                    if (v) next.set('search', v); else next.delete('search');
                    next.set('page', '1'); // Reset to first page
                    resetPaginationState();
                    setSearchParams(next);
                  }
                }}
              />

              <button
                  className="text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors float-right"
                  onClick={() => {
                    // Reset URL parameters to defaults
                    setSearchParams(new URLSearchParams({ 
                      layer: 'things', 
                      minConfidence: '0', 
                      cluster: 'false',
                      page: '1',
                      limit: '50'
                    }));
                    
                    // Clear all UI state
                    resetPaginationState();
                    setDetail(null);
                    setSelectedItem(null);
                    setSelectedId(null);
                    setRelatedData(null);
                    setRelatedView('default');
                    setShowObsCount(10);
                    setTaxaQ('');
                    setTaxaOpts([]);
                    setError(null);
                    setItems([]);
                    setClusters([]);
                    setMessagePins([]);
                    setRelatedLoading(false);
                    setDetailLoading(false);
                    setLoading(false);
                    setPinClickLoading(false);
                    
                    // Clear cache and reset first load
                    cacheRef.current.clear();
                    firstLoadRef.current = true;
                  }}
                >
                  Reset
              </button>

              {/* Advanced Filters - Progressive disclosure */}
              <details className="group">
                <summary className="text-xs font-medium text-gray-600 cursor-pointer hover:text-gray-900 transition-colors select-none">
                  Advanced filters
                </summary>

                <div className="mt-3 space-y-3 pl-4 mb-4 border-l-2 border-gray-200">
                  {/* Project Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Project ID</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Enter project ID..."
                        defaultValue={query.project_id}
                        className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const v = e.currentTarget.value || '';
                            const next = new URLSearchParams(searchParams);
                            if (v) next.set('project_id', v); else next.delete('project_id');
                            // Close detail panel when changing project filter
                            setDetail(null);
                            setSelectedItem(null);
                            setSelectedId(null);
                            next.set('page', '1'); // Reset to first page
                            resetPaginationState();
                            setSearchParams(next);
                            cacheRef.current.clear();
                          }
                        }}
                      />
                      {query.project_id && (
                        <button
                          className="px-2 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-sm transition-colors"
                          onClick={() => {
                            const next = new URLSearchParams(searchParams);
                            next.delete('project_id');
                            next.set('page', '1'); // Reset to first page
                            setDetail(null);
                            setSelectedItem(null);
                            setSelectedId(null);
                            resetPaginationState();
                            setSearchParams(next);
                            cacheRef.current.clear();
                          }}
                          title="Clear project filter"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Confidence */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Confidence: <span className="font-mono text-gray-900">{parseFloat(query.minConfidence).toFixed(2)}</span>
                    </label>
                    <input
                      type="range"
                      step="0.05"
                      min="0"
                      max="1"
                      className="w-full h-1 bg-gray-200 rounded-sm appearance-none cursor-pointer"
                      value={query.minConfidence}
                      onChange={(e) => {
                        const next = new URLSearchParams(searchParams);
                        next.set('minConfidence', e.currentTarget.value);
                        next.set('page', '1'); // Reset to first page
                        resetPaginationState();
                        setSearchParams(next);
                      }}
                    />
                  </div>

                  {/* Taxa Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Taxa</label>
                    <input
                      type="text"
                      placeholder="Search taxa..."
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                      value={taxaQ}
                      onChange={(e) => setTaxaQ(e.currentTarget.value)}
                    />
                    {taxaQ && taxaOpts.length > 0 && (
                      <div className="mt-1 border rounded-sm bg-white shadow-sm max-h-40 overflow-auto">
                        {taxaOpts.map((t) => (
                          <div
                            key={t.id}
                            className="px-2 py-1.5 text-xs hover:bg-gray-50 cursor-pointer"
                            onClick={() => {
                              const next = new URLSearchParams(searchParams);
                              next.set('taxon', t.id);
                              next.set('layer', 'detections');
                              next.set('page', '1'); // Reset to first page
                              resetPaginationState();
                              setSearchParams(next);
                              setTaxaQ('');
                              setTaxaOpts([]);
                            }}
                          >
                            {t.canonical_name} {t.common_name ? `• ${t.common_name}` : ''}
                          </div>
                        ))}
                      </div>
                    )}
                    {searchParams.get('taxon') && (
                      <button
                        className="mt-1 text-xs text-blue-600 hover:text-blue-800"
                        onClick={() => { 
                          const next = new URLSearchParams(searchParams); 
                          next.delete('taxon'); 
                          next.set('page', '1'); // Reset to first page
                          resetPaginationState();
                          setSearchParams(next); 
                        }}
                      >
                        Clear taxa filter
                      </button>
                    )}
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Category</label>
                    <select
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                      value={query.category}
                      onChange={(e) => {
                        const next = new URLSearchParams(searchParams);
                        const v = e.currentTarget.value;
                        if (v) next.set('category', v); else next.delete('category');
                        next.set('page', '1'); // Reset to first page
                        resetPaginationState();
                        setSearchParams(next);
                      }}
                    >
                      <option value="">All categories</option>
                      <option value="organism">Organism</option>
                      <option value="structure">Structure</option>
                      <option value="environment">Environment</option>
                      <option value="weather">Weather</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Date Range */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
                    <input
                      type="datetime-local"
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                      onChange={(e) => {
                        const v = e.currentTarget.value;
                        const next = new URLSearchParams(searchParams);
                        if (v) next.set('from', new Date(v).toISOString()); 
                        else next.delete('from');
                        next.set('page', '1'); // Reset to first page
                        resetPaginationState();
                        setSearchParams(next);
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
                    <input
                      type="datetime-local"
                      className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                      onChange={(e) => {
                        const v = e.currentTarget.value;
                        const next = new URLSearchParams(searchParams);
                        if (v) next.set('to', new Date(v).toISOString()); 
                        else next.delete('to');
                        next.set('page', '1'); // Reset to first page
                        resetPaginationState();
                        setSearchParams(next);
                      }}
                    />
                  </div>

                  {/* Options */}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={query.cluster !== 'false'}
                        onChange={(e) => {
                          const next = new URLSearchParams(searchParams);
                          next.set('cluster', e.currentTarget.checked ? 'true' : 'false');
                          next.set('page', '1'); // Reset to first page
                          resetPaginationState();
                          setSearchParams(next);
                        }}
                        className="rounded border-gray-300"
                      />
                      Enable clustering
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!searchParams.get('visibility')}
                        onChange={(e) => {
                          const next = new URLSearchParams(searchParams);
                          if (e.currentTarget.checked) next.delete('visibility'); 
                          else next.set('visibility', 'public');
                          next.set('page', '1'); // Reset to first page
                          resetPaginationState();
                          setSearchParams(next);
                        }}
                        className="rounded border-gray-300"
                      />
                      Show all (public + private)
                    </label>
                  </div>
                </div>
              </details>
            </div>
          </div>

          {/* RESULTS LIST - Optimized hierarchy */}
          <div className="flex-1 overflow-auto">

            <div className="text-xs text-gray-500 font-medium px-6 py-2 bg-gray-100 flex items-center justify-between border-b">
              <span>
                {loading ? 'Loading...' : `${items.length} of ${totalCount} items`}
              </span>
              {/* {!loading && items.length > 0 && (
                <span className="text-gray-400 font-normal">
                  {query.layer === 'detections' && 'AI findings from photos'}
                  {query.layer === 'observations' && 'Photo uploads'}
                  {query.layer === 'things' && 'Tracked entities'}
                </span>
              )} */}
            </div>

            {loading && (
              <div className="p-6 space-y-3">
                {[1,2,3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-2 bg-gray-100 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            )}
            
            {error && (
              <div className="p-6 text-sm text-red-600 bg-red-50 border-t border-red-100">{error}</div>
            )}

            {!loading && items.length === 0 && (
              <div className="p-12 text-center">
                <div className="text-sm text-gray-400">No results found</div>
              </div>
            )}

            <ul className="divide-y divide-gray-100" ref={listRef}>
              {items.map((it) => {
                // Debug logging for image investigation
                // console.log('[ThingsExplorer] Detection item:', {
                //   id: it.id,
                //   canonical_name: it.canonical_name,
                //   sample_images: it.sample_images,
                //   image_url: it.image_url,
                //   hasSampleImages: Array.isArray(it.sample_images) && it.sample_images.length > 0,
                //   hasImageUrl: !!it.image_url,
                //   hasAnyImage: !!(it.sample_images?.[0] || it.image_url),
                //   layer: query.layer
                // });
                
                return (
                  <li
                    key={it.id}
                    data-item-key={it.id}
                    className={`group transition-colors cursor-pointer ${
                      selectedId === it.id 
                        ? 'bg-gray-100' 
                        : 'bg-white hover:bg-gray-50'
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleItemClick(it)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleItemClick(it); }}
                  >
                    <div className="p-5 flex gap-3">
                      {/* Image - Visual anchor */}
                      {Array.isArray(it.sample_images) && it.sample_images.length > 0 ? (
                        <img 
                          src={getImageUrl(it.sample_images[0])} 
                          alt="" 
                          className="w-12 h-12 object-cover rounded-xl flex-shrink-0 border border-gray-200" 
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex-shrink-0 border border-gray-200"></div>
                      )}

                    {/* Content - Clear hierarchy */}
                    <div className="flex-1 min-w-0">
                      {/* Primary: Name */}
                      <div className="font-medium text-gray-900 truncate mb-1">
                        {it.canonical_name || 'Unnamed'}
                      </div>
                      
                      {/* Secondary: Time */}
                      <div className="text-xs text-gray-500 font-mono">
                        {it.last_seen_at ? new Date(it.last_seen_at).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        }) : '—'}
                      </div>

                      {/* Taxonomy snippet for things */}
                      {query.layer === 'things' && it.taxonomic_classification && (
                        <div className="mt-1 text-[11px] text-gray-600 truncate">
                          {(it.taxonomic_classification.genus || it.taxonomic_classification.species) && (
                            <>
                              {it.taxonomic_classification.genus ? `${it.taxonomic_classification.genus}` : ''}
                              {it.taxonomic_classification.species ? ` ${it.taxonomic_classification.species}` : ''}
                            </>
                          )}
                        </div>
                      )}

                      {/* Tertiary: Metadata (subtle) */}
                      {/* <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                        {it.confidence != null && (
                          <span>{(it.confidence * 100).toFixed(0)}%</span>
                        )}
                        {query.layer === 'things' && it.observation_count > 0 && (
                          <span>{it.observation_count} obs</span>
                        )}
                        {(query.layer === 'detections' || query.layer === 'observations') && (
                          <span className="text-gray-400">From 1 photo</span>
                        )}
                      </div> */}
                    </div>
                  </div>
                </li>
                );
              })}
            </ul>

            {/* Load More Button */}
            {hasMore && !loading && (
              <div className="p-4 border-t border-gray-100">
                <button
                  className="w-full text-sm text-gray-600 hover:text-gray-900 font-medium py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loadingMore}
                  onClick={async () => {
                    try {
                      setLoadingMore(true);
                      const currentPage = parseInt(query.page) || 1;
                      const nextPage = currentPage + 1;
                      
                      const next = new URLSearchParams(searchParams);
                      next.set('page', nextPage.toString());
                      setSearchParams(next);
                    } catch (e) {
                      console.warn('[ThingsExplorer] Load more failed:', e);
                    } finally {
                      setLoadingMore(false);
                    }
                  }}
                >
                  {loadingMore ? 'Loading...' : `Load more (${totalCount - items.length} remaining)`}
                </button>
              </div>
            )}
          </div>
        </div>
      <div className="flex-1">
        <div className="w-full h-full border rounded overflow-hidden relative">
          <UnifiedMap
            layoutContext="explore"
            onMapInstance={(map) => { mapRef.current = map; }}
            showMessagePins={true}
            messagePins={messagePins}
            minZoomForPins={3}
            onPinClick={(id, meta) => handlePinClick(id, meta)}
            center={parseBBoxParam(query.bbox) ? [
              (parseBBoxParam(query.bbox)[1] + parseBBoxParam(query.bbox)[3]) / 2,
              (parseBBoxParam(query.bbox)[0] + parseBBoxParam(query.bbox)[2]) / 2,
            ] : undefined}
          />
          {parseBBoxParam(query.bbox) && (
            <div className="absolute top-2 left-2 bg-white/90 backdrop-blur px-2 py-1 rounded text-xs border shadow">
              BBOX {query.bbox}
            </div>
          )}
          {/* Fit-to-results floating button */}
          <button
            className="absolute bottom-4 right-4 bg-white border shadow px-3 py-2 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || pinClickLoading}
            onClick={() => {
              try {
                if (!mapRef.current) return;
                const latlngs = [];
                messagePins.forEach((p) => { if (p.location) latlngs.push([p.location.lat, p.location.lng]); });
                if (latlngs.length > 0 && window.L && window.L.latLngBounds) {
                  const b = window.L.latLngBounds(latlngs);
                  mapRef.current.fitBounds(b, { padding: [20, 20] });
                }
              } catch (e) {
                console.warn('[ThingsExplorer] Fit to results failed:', e);
              }
            }}
          >
            {loading || pinClickLoading ? 'Loading...' : 'Fit to results'}
          </button>
          {/* Detail side panel - UNIFORM STRUCTURE */}
          {(detail || (selectedItem && (query.layer === 'detections' || query.layer === 'observations'))) && (
            <div className="absolute top-0 right-0 w-96 max-w-full h-full bg-white border-l shadow-xl flex flex-col z-[20000] pointer-events-auto">
              {/* FIXED HEADER */}
              <div className="flex-shrink-0 px-6 py-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xl font-light text-black uppercase tracking-wider">
                      {query.layer === 'detections' ? 'Detection' : query.layer === 'observations' ? 'Observation' : 'Thing'}
                    </div>
                    {/* <div className="text-xs text-gray-500 mt-1">
                      {query.layer === 'detections' && 'AI finding within a photo'}
                      {query.layer === 'observations' && 'Complete photo with metadata'}
                      {query.layer === 'things' && 'Entity tracked across multiple photos'}
                    </div> */}
                   
                    
                  </div>
                  <button 
                      className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors border border-gray-200 rounded-full px-2 py-1" 
                      onClick={() => { setDetail(null); setSelectedItem(null); }}
                    >
                      X
                    </button>
                  
                  
                </div>
                {/* <div className="mt-2">
                  <div className="text-xs text-blue-800 leading-relaxed">
                    {query.layer === 'detections' && (
                      <span>Individual AI findings within photos. One photo can contain multiple detections.</span>
                    )}
                    {query.layer === 'observations' && (
                      <span>Complete photos with location and time. Each upload creates one observation.</span>
                    )}
                    {query.layer === 'things' && (
                      <span>Real-world entities tracked over time. Multiple detections aggregate into one thing.</span>
                    )}
                  </div>
                </div> */}
              </div>

              {detailLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-sm text-gray-400">Loading...</div>
                </div>
              ) : (
                <div className="flex-1 overflow-auto">
                  {/* PRIMARY CARD - Always uniform regardless of layer */}
                  <div className="px-6 py-5">
                    {/* Hero Image */}
                    {(((selectedItem && ((selectedItem.sample_images && selectedItem.sample_images[0]) || selectedItem.image_url))) || 
                      ((detail?.sample_images && detail.sample_images[0]))) && (
                      <div className="mb-4 -mx-6 -mt-5">
                        {(query.layer === 'detections' || query.layer === 'things') && selectedItem?.bbox ? (
                          <>
                            {process.env.NODE_ENV !== 'production' && console.debug('[ThingsExplorer] Using BBoxOverlay', { 
                              layer: query.layer, 
                              selectedItemBbox: selectedItem.bbox,
                              selectedItemId: selectedItem.id 
                            })}
                            <BBoxOverlay
                              src={getImageUrl(selectedItem?.sample_images?.[0] || selectedItem?.image_url || detail.sample_images[0])}
                              bbox={selectedItem.bbox}
                              bboxes={selectedItem.bboxes}
                              alt=""
                              className="w-full h-48"
                            />
                          </>
                        ) : (
                          <>
                            {process.env.NODE_ENV !== 'production' && console.debug('[ThingsExplorer] Using regular image', { 
                              layer: query.layer, 
                              hasSelectedItem: !!selectedItem,
                              hasBbox: !!selectedItem?.bbox,
                              selectedItemId: selectedItem?.id 
                            })}
                            <img 
                              src={getImageUrl(selectedItem?.sample_images?.[0] || selectedItem?.image_url || detail.sample_images[0])} 
                              alt="" 
                              className="w-full h-48 object-contain bg-gray-50" 
                            />
                          </>
                        )}
                      </div>
                    )}

                    {/* Primary Name */}
                    <h2 className="text-xl font-light tracking-tight text-gray-900 mb-3">
                      {query.layer === 'detections' 
                        ? (selectedItem?.canonical_name || selectedItem?.label || 'Unnamed') 
                        : (detail?.canonical_name || 'Unnamed')}
                    </h2>

                    {/* Key Metadata Grid */}
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
                      {selectedItem && (query.layer === 'detections') && selectedItem.confidence != null && (
                        <>
                          <dt className="text-gray-500 uppercase tracking-wider font-semibold">Confidence</dt>
                          <dd className="text-gray-900 font-mono text-right">{(selectedItem.confidence * 100).toFixed(0)}%</dd>
                        </>
                      )}
                      {/* Hide confidence for Things for now (detail.confidence) */}
                      {(query.layer === 'detections' && selectedItem?.category) ? (
                        <>
                          <dt className="text-gray-500 uppercase tracking-wider font-semibold">Type</dt>
                          <dd className="text-gray-900 text-right capitalize">{selectedItem.category}</dd>
                        </>
                      ) : (query.layer === 'things' && detail?.type ? (
                        <>
                          <dt className="text-gray-500 uppercase tracking-wider font-semibold">Type</dt>
                          <dd className="text-gray-900 text-right capitalize">{detail.type}</dd>
                        </>
                      ) : null)}
                      {(selectedItem?.last_seen_at || detail.last_seen_at) && (
                        <>
                          <dt className="text-gray-500 uppercase tracking-wider font-semibold">Captured</dt>
                          <dd className="text-gray-900 text-right">
                            {new Date(selectedItem?.last_seen_at || detail.last_seen_at).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </dd>
                        </>
                      )}
                      {((selectedItem?.lat != null && selectedItem?.lng != null) || (detail.lat != null && detail.lng != null)) && (
                        <>
                          <dt className="text-gray-500 uppercase tracking-wider font-semibold">Location</dt>
                          <dd className="text-gray-900 font-mono text-right text-xs">
                            {(selectedItem?.lat || detail.lat).toFixed(3)}, {(selectedItem?.lng || detail.lng).toFixed(3)}
                          </dd>
                        </>
                      )}
                      {selectedItem?.user_display && (query.layer === 'observations' || query.layer === 'detections') && (
                        <>
                          <dt className="text-gray-500 uppercase tracking-wider font-semibold">Observer</dt>
                          <dd className="text-right">
                            <a 
                              href={selectedItem.username ? `/users/${selectedItem.username}` : `/profile/${selectedItem.user_id}`}
                              className="inline-flex items-center gap-1.5 hover:underline text-gray-900"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {selectedItem.user_avatar ? (
                                <img 
                                  src={getImageUrl(selectedItem.user_avatar)} 
                                  alt={selectedItem.user_display}
                                  className="w-5 h-5 rounded-full object-cover border border-gray-200"
                                />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-600 font-medium">
                                  {selectedItem.user_display ? selectedItem.user_display.charAt(0).toUpperCase() : 'U'}
                                </div>
                              )}
                              <span>{selectedItem.user_display}</span>
                            </a>
                          </dd>
                        </>
                      )}
                      {selectedItem?.project_id && selectedItem?.project_name && (query.layer === 'observations' || query.layer === 'detections') && (
                        <>
                          <dt className="text-gray-500 uppercase tracking-wider font-semibold">Project</dt>
                          <dd className="text-right">
                            <a 
                              href={`/projects/${selectedItem.project_id}`}
                              className="inline-flex items-center gap-1.5 hover:underline text-gray-900"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>{selectedItem.project_name}</span>
                            </a>
                          </dd>
                        </>
                      )}
                      {query.layer === 'things' && detail.status && (
                        <>
                          <dt className="text-gray-500 uppercase tracking-wider font-semibold">Status</dt>
                          <dd className="text-gray-900 text-right capitalize">{detail.status}</dd>
                        </>
                      )}

                      {/* Hide matched thing row (redundant) */}

                      {/* Hide Top Detection row in observations */}

                      {query.layer === 'observations' && (
                        <>
                          {/* Show observation-level privacy fields*/}
                          {selectedItem?.visibility && (
                            <>
                              <dt className="text-gray-500 uppercase tracking-wider font-semibold">Visibility</dt>
                              <dd className="text-gray-900 text-right capitalize">{selectedItem.visibility}</dd>
                            </>
                          )}
                          {selectedItem?.license && (
                            <>
                              <dt className="text-gray-500 uppercase tracking-wider font-semibold">License</dt>
                              <dd className="text-gray-900 text-right">{selectedItem.license}</dd>
                            </>
                          )}
                        </>
                      )}
                    </dl>

                    {/* AI Description Section */}
                    {selectedItem?.description && query.layer === 'detections' && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">AI Description</h3>
                        <div className="text-sm text-gray-700 leading-relaxed">
                          {selectedItem.description}
                        </div>
                      </div>
                    )}

                    {/* Taxonomic Classification Section */}
                    {selectedItem?.taxonomic_classification && query.layer === 'detections' && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Taxonomic Classification</h3>
                        <div className="space-y-6">
                          {/* Domain */}
                          {selectedItem.taxonomic_classification.domain && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</span>
                              <span className="text-sm text-gray-900 capitalize">{selectedItem.taxonomic_classification.domain}</span>
                            </div>
                          )}
                          
                          {/* Hierarchy */}
                          {detectionHierarchyEntries.length > 0 && (
                            <div className="space-y-2">
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Classification</span>
                              <div className="text-sm text-gray-700 space-y-1">
                                {detectionHierarchyEntries.map(([key, value], idx) => (
                                  <div key={`${key || 'entry'}-${idx}`} className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500 capitalize min-w-[4rem]">
                                      {key || `Level ${idx + 1}`}:
                                    </span>
                                    <span className="text-gray-900">{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Common Names */}
                          {selectedItem.taxonomic_classification.common_names && selectedItem.taxonomic_classification.common_names.length > 0 && (
                            <div className="space-y-2">
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Common Names</span>
                              <div className="text-sm text-gray-700">
                                {selectedItem.taxonomic_classification.common_names.join(', ')}
                              </div>
                            </div>
                          )}
                          
                          {/* Taxonomic Confidence */}
                          {selectedItem.taxonomic_confidence && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Taxonomic Confidence</span>
                              <span className={`text-sm px-2 py-1 rounded-full text-xs font-medium ${
                                selectedItem.taxonomic_confidence === 'high' ? 'bg-green-100 text-green-800' :
                                selectedItem.taxonomic_confidence === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {selectedItem.taxonomic_confidence}
                              </span>
                            </div>
                          )}
                          
                          {/* Expert Review Flag */}
                          {selectedItem.requires_expert_review && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Expert Review</span>
                              <span className="text-sm px-2 py-1 rounded-full bg-orange-100 text-orange-800 text-xs font-medium">
                                Required
                              </span>
                            </div>
                          )}
                          
                          {/* Identification Notes */}
                          {selectedItem.identification_notes && (
                            <div className="space-y-2">
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Identification Notes</span>
                              <div className="text-sm text-gray-700 leading-relaxed">
                                {selectedItem.identification_notes}
                              </div>
                            </div>
                          )}
                          
                          {/* Common Confusion Species */}
                          {selectedItem.common_confusion_species && selectedItem.common_confusion_species.length > 0 && (
                            <div className="space-y-2">
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Common Confusion Species</span>
                              <div className="text-sm text-gray-700">
                                {selectedItem.common_confusion_species.join(', ')}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Taxonomic Classification for Things (all domains) */}
                    {detail?.taxonomic_classification && query.layer === 'things' && (
                      <div className="mt-6 pt-4 border-t border-gray-200">
                        <h3 className="text-sm font-medium text-gray-900 mb-2">Taxonomic Classification</h3>
                        <div className="space-y-3">
                          {/* Domain (if present) */}
                          {detail.taxonomic_classification.domain && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</span>
                              <span className="text-sm text-gray-900 capitalize">{detail.taxonomic_classification.domain}</span>
                            </div>
                          )}
                          {/* Hierarchy (supports multiple shapes) */}
                          {thingHierarchyEntries.length > 0 && (
                            <div className="space-y-1 text-sm text-gray-700">
                              {thingHierarchyEntries.map(([key, value], idx) => (
                                value ? (
                                  <div key={`${key || 'entry'}-${idx}`} className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500 capitalize w-24">{key || `Level ${idx + 1}`}:</span>
                                    <span className="text-gray-900 capitalize">{String(value)}</span>
                                  </div>
                                ) : null
                              ))}
                            </div>
                          )}
                          {/* Common names (if present) */}
                          {Array.isArray(detail.taxonomic_classification.common_names) && detail.taxonomic_classification.common_names.length > 0 && (
                            <div className="space-y-1 text-sm text-gray-700">
                              <span className="text-xs text-gray-500 uppercase tracking-wider">Common Names</span>
                              <div>{detail.taxonomic_classification.common_names.join(', ')}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* UNIFIED RELATED CONTENT SECTION */}
                  <div className="px-6 py-5 border-t">
                    <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
                      Related
                    </h3>

                    {/* Tab Selector */}
                    <div className="flex gap-2 mb-4">
                      {query.layer === 'things' && (
                        <>
                          <button
                            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                              relatedView === 'default' || relatedView === 'observations'
                                ? 'bg-gray-900 text-white' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => setRelatedView('observations')}
                          >
                            Observations ({detail?.observation_count || 0})
                          </button>
                          <button
                            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                              relatedView === 'detections'
                                ? 'bg-gray-900 text-white' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => {
                              setRelatedView('detections');
                              fetchRelatedData('detections', detail?.id);
                            }}
                          >
                            Detections ({detail?.detection_count || 0})
                          </button>
                        </>
                      )}
                      {query.layer === 'observations' && (
                        <>
                          <button
                            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                              relatedView === 'default' || relatedView === 'things'
                                ? 'bg-gray-900 text-white' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => setRelatedView('things')}
                          >
                            Thing {selectedItem?.thing_id ? '(1)' : '(0)'}
                          </button>
                          <button
                            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                              relatedView === 'detections'
                                ? 'bg-gray-900 text-white' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => {
                              setRelatedView('detections');
                              fetchRelatedData('detections', selectedItem?.id);
                            }}
                          >
                            Detections ({relatedData?.detections?.length || 0})
                          </button>
                        </>
                      )}
                      {query.layer === 'detections' && (
                        <>
                          <button
                            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                              relatedView === 'default' || relatedView === 'things'
                                ? 'bg-gray-900 text-white' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => setRelatedView('things')}
                          >
                            Thing {selectedItem?.thing_id ? '(1)' : '(0)'}
                          </button>
                          <button
                            className={`px-3 py-1.5 text-xs font-medium rounded-sm transition-all ${
                              relatedView === 'observations'
                                ? 'bg-gray-900 text-white' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => {
                              setRelatedView('observations');
                              fetchRelatedData('observations', selectedItem?.observation_id);
                            }}
                          >
                            Observation {selectedItem?.observation_id ? '(1)' : '(0)'}
                          </button>
                        </>
                      )}
                    </div>

                    {/* Content based on layer and view */}
                    
                    {/* THINGS LAYER */}
                    {query.layer === 'things' && (
                      <>
                        {/* Observations tab */}
                        {(relatedView === 'default' || relatedView === 'observations') && (
                          <div>
                            {detailLoading ? (
                              <div className="text-xs text-gray-400 text-center py-4">Loading observations...</div>
                            ) : detail?.observations && detail.observations.length > 0 ? (
                              <ul className="space-y-2">
                                {detail.observations.slice(0, showObsCount).map((o) => (
                                  <li key={o.id}>
                                    <RelatedCard
                                      item={o}
                                      type="observation"
                                      onNavigate={handleRelatedItemNavigate}
                                    />
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="text-xs text-gray-400 text-center py-4">No observations found</div>
                            )}
                            {detail?.observations && detail.observations.length > showObsCount && (
                              <button
                                className="mt-3 w-full text-xs text-gray-600 hover:text-gray-900 font-medium py-2 border border-gray-200 rounded-sm hover:bg-gray-50 transition-colors"
                                onClick={() => setShowObsCount(prev => prev + 10)}
                              >
                                Load more ({detail.observations.length - showObsCount} remaining)
                              </button>
                            )}
                          </div>
                        )}

                        {/* Detections tab */}
                        {relatedView === 'detections' && (
                          <div>
                            {relatedLoading ? (
                              <div className="text-xs text-gray-400 text-center py-4">Loading detections...</div>
                            ) : relatedData?.detections && relatedData.detections.length > 0 ? (
                              <ul className="space-y-2">
                                {relatedData.detections.map((d) => (
                                  <li key={d.id}>
                                    <RelatedCard
                                      item={d}
                                      type="detection"
                                      onNavigate={handleRelatedItemNavigate}
                                    />
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="text-xs text-gray-400 text-center py-4">No detections found</div>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* OBSERVATIONS LAYER */}
                    {query.layer === 'observations' && (
                      <>
                        {/* Thing tab */}
                        {(relatedView === 'default' || relatedView === 'things') && (
                          <div>
                            {selectedItem?.thing_id && detail ? (
                              <RelatedCard
                                item={detail}
                                type="thing"
                                onNavigate={handleRelatedItemNavigate}
                              />
                            ) : (
                              <div className="text-xs text-gray-400 text-center py-4">No thing matched yet</div>
                            )}
                          </div>
                        )}

                        {/* Detections tab */}
                        {relatedView === 'detections' && (
                          <div>
                            {relatedLoading ? (
                              <div className="text-xs text-gray-400 text-center py-4">Loading detections...</div>
                            ) : relatedData?.detections && relatedData.detections.length > 0 ? (
                              <ul className="space-y-2">
                                {relatedData.detections.map((d) => (
                                  <li key={d.id}>
                                    <RelatedCard
                                      item={{ ...d, captured_at: relatedData.captured_at, image_url: relatedData.image_url }}
                                      type="detection"
                                      onNavigate={handleRelatedItemNavigate}
                                    />
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className="text-xs text-gray-400 text-center py-4">No detections found</div>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* DETECTIONS LAYER */}
                    {query.layer === 'detections' && (
                      <>
                        {/* Thing tab */}
                        {(relatedView === 'default' || relatedView === 'things') && (
                          <div>
                            {selectedItem?.thing_id && detail ? (
                              <RelatedCard
                                item={detail}
                                type="thing"
                                onNavigate={handleRelatedItemNavigate}
                              />
                            ) : (
                              <div className="text-xs text-gray-400 text-center py-4">No thing matched yet</div>
                            )}
                          </div>
                        )}

                        {/* Observation tab */}
                        {relatedView === 'observations' && (
                          <div>
                            {relatedLoading ? (
                              <div className="text-xs text-gray-400 text-center py-4">Loading observation...</div>
                            ) : relatedData ? (
                              <RelatedCard
                                item={relatedData}
                                type="observation"
                                onNavigate={handleRelatedItemNavigate}
                              />
                            ) : (
                              <div className="text-xs text-gray-400 text-center py-4">No observation data</div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


